import React from "react";
import { SupportedLocale } from "../lib/content";

type HubCard = {
  title: string;
  description: string;
  priceFrom: string;
  href?: string;
  ctaLabel: string;
};

type Props = {
  locale?: SupportedLocale;
};

export const ZwierzetaCywilHub: React.FC<Props> = ({ locale = "pl" }) => {
  const isEn = locale === "en";
  const isEs = locale === "es";

  const pageTitle = isEn
    ? "Civil cases involving animals"
    : isEs
    ? "Asuntos civiles relacionados con animales"
    : "Sprawy cywilne dotyczące zwierząt";

  const pageIntro = isEn
    ? "Choose the case you need help with"
    : isEs
    ? "Elige el asunto en el que necesitas ayuda"
    : "Wybierz sprawę, w której potrzebujesz pomocy";

  const cards: HubCard[] = [
    {
      title: isEn ? "Personal injury" : isEs ? "Daño personal" : "Szkoda na osobie",
      description: isEn
        ? "Have you been attacked by an animal? Have you suffered a health injury (e.g. a dog bite)? Are you wondering what you can do to obtain compensation or non-pecuniary damages?"
        : isEs
        ? "¿Has sido atacado por un animal? ¿Has sufrido un daño en la salud (p. ej., una mordedura)? ¿Te preguntas qué puedes hacer para obtener indemnización o compensación por daño moral?"
        : "Zostałeś zaatakowany przez zwierzę? Doznałeś uszczerbku na zdrowiu (np. w wyniku pogryzienia przez psa)? Zastanawiasz się, co możesz zrobić, by uzyskać odszkodowanie lub zadośćuczynienie?",
      priceFrom: isEn ? "Consultation from PLN 500" : isEs ? "Consulta desde 500 PLN" : "Konsultacja od 500 zł",
      href: "/zwierzeta/cywil/pogryzienie-czlowieka",
      ctaLabel: isEn ? "See how we help" : isEs ? "Ver cómo ayudamos" : "Sprawdź, jak pomagamy",
    },
    {
      title: isEn ? "Injury to an animal" : isEs ? "Daño a un animal" : "Szkoda na zwierzęciu",
      description: isEn
        ? "Has your animal been attacked (e.g. bitten) and suffered a health injury? Are you wondering what legal steps to take to obtain compensation or non-pecuniary damages?"
        : isEs
        ? "¿Tu animal ha sido atacado (p. ej., mordido) y ha sufrido un daño en la salud? ¿Te preguntas qué pasos legales dar para obtener indemnización o daño moral?"
        : "Twoje zwierzę zostało zaatakowane (np. pogryzione), w wyniku czego doznało uszczerbku na zdrowiu? Zastanawiasz sie, jakie kroki prawne  podjąć, by uzyskać odszkodowanie lub zadośćuczynienie?",
      priceFrom: isEn ? "Consultation from PLN 450" : isEs ? "Consulta desde 450 PLN" : "Konsultacja od 450 zł",
      href: "/zwierzeta/cywil/pogryzienie-zwierzecia",
      ctaLabel: isEn ? "See how we help" : isEs ? "Ver cómo ayudamos" : "Sprawdź, jak pomagamy",
    },
    {
      title: isEn ? 'Warranty for a “defective” animal' : isEs ? 'Garantía por animal “defectuoso”' : "Rękojmia za „wadliwe” zwierzę",
      description: isEn
        ? "Have you bought an animal but it turned out to be different than the contract or the seller’s assurances? Are you wondering what legal steps to take to recover unexpected treatment costs (not disclosed by the seller), request a price reduction (e.g. the animal is not purebred) or even return the animal for a refund (possible only in specific cases)?"
        : isEs
        ? "¿Has comprado un animal pero resultó ser diferente de lo que decía el contrato o las aseguraciones del vendedor? ¿Qué pasos legales dar para recuperar costes de tratamiento no informados, pedir rebaja del precio (p. ej., no es de raza) o incluso devolver el animal a cambio del precio (solo en casos especiales)?"
        : "Kupiłeś zwierzę, ale okazało się, że jest ono inne, niż wynika z umowy lub zapewnień sprzedawcy? Zastanawiasz się, jakie kroki prawne podjąć, by uzyskać zwrot nieprzewidzianych kosztów leczenia (o których nie informował sprzedawca), żądać obniżenia ceny sprzedaży (bo np. zwierzę nie jest rasowe) lub nawet zwrócić zwierzę za zwrotem ceny (możliwe tylko w szczególnych wypadkach)? ",
      priceFrom: isEn ? "Consultation from PLN 450" : isEs ? "Consulta desde 450 PLN" : "Konsultacja od 450 zł",
      href: "/zwierzeta/cywil/rekojmia-zwierze",
      ctaLabel: isEn ? "See how we help" : isEs ? "Ver cómo ayudamos" : "Sprawdź, jak pomagamy",
    },
    {
      title: isEn ? "Pet dispute after a breakup" : isEs ? "Disputa por un animal tras la ruptura" : "Spór o zwierzę po rozstaniu",
      description: isEn
        ? "Have you separated (formal or informal relationship)? Did you raise an animal together? Are you wondering who the animal belongs to under the law and want to regulate its legal status?"
        : isEs
        ? "¿Te has separado (relación formal o informal)? ¿Criabais un animal juntos? ¿Te preguntas de quién es legalmente y quieres regular su situación jurídica?"
        : "Rozstałeś się ze kimś (ze związku formalnego albo nieformalnego)? Razem z tę osobą chowałeś zwierzę? Zastanawiasz się, czyje, w świetle prawa, jest to zwierzę? Chcesz uregulować jego prawną sytuację?",
      priceFrom: isEn ? "Consultation from PLN 500" : isEs ? "Consulta desde 500 PLN" : "Konsultacja od 500 zł",
      href: "/zwierzeta/cywil/spor-o-psa-po-rozstaniu",
      ctaLabel: isEn ? "See how we help" : isEs ? "Ver cómo ayudamos" : "Sprawdź, jak pomagamy",
    },
    {
      title: isEn ? "Veterinary malpractice" : isEs ? "Mala praxis veterinaria" : "Błąd weterynaryjny",
      description: isEn
        ? "Has your animal suffered a health injury or died as a result of a veterinary procedure? Are you wondering what legal steps to take to obtain compensation or non-pecuniary damages? Or are you a veterinarian accused of malpractice?"
        : isEs
        ? "¿Tu animal sufrió un daño en la salud o murió a raíz de un procedimiento veterinario? ¿Qué pasos legales dar para obtener indemnización o daño moral? ¿O eres veterinario acusado de mala praxis?"
        : "Twoje zwierzę doznało uszczerbku na zdrowiu albo zmarło wskutek zabiegu weterynaryjnego? Zastanawiasz się, jakie kroki prawne podjąć, by uzyskać odszkodowanie albo zadośćuczynienie? A możesz jesteś weterynarzem, pomawianym o błąd w sztuce?",
      priceFrom: isEn ? "Consultation from PLN 450" : isEs ? "Consulta desde 450 PLN" : "Konsultacja od 450 zł",
      href: "/zwierzeta/cywil/blad-weterynaryjny",
      ctaLabel: isEn ? "See how we help" : isEs ? "Ver cómo ayudamos" : "Sprawdź, jak pomagamy",
    },

    {
      title: isEn
        ? "Improper animal care (hotel, behaviourist, petsitter)"
        : isEs
        ? "Atención inadecuada (hotel, etólogo, petsitter)"
        : "Niewłaściwa opieka nad zwierzęciem (hotel, behawiorysta, petsitter)",
      description: isEn
        ? "Has your animal suffered a health injury or died due to improper care at a hotel, behaviourist or petsitter? Are you wondering what legal steps to take? Or are you a hotel owner/behaviourist/petsitter accused of improper care?"
        : isEs
        ? "¿Tu animal sufrió un daño en la salud o murió por una atención inadecuada en hotel, con etólogo o petsitter? ¿Qué pasos legales tomar? ¿O eres propietario/etólogo/petsitter acusado de atención inadecuada?"
        : "Twoje zwierzę doznało uszczerbku na zdrowiu albo zmarło wskutek niewłaściwej opieki w hotelu, u behawiorysty lub petsittera? Zastanawiasz się, jakie kroki prawne podjąć? A może jesteś właścicielem hotelu, behawiorystą, petsitterem, pomawianym o niewłaściwą opiekę nad zwierzęciem?",
      priceFrom: isEn ? "Consultation from PLN 400" : isEs ? "Consulta desde 400 PLN" : "Konsultacja od 400 zł",
      ctaLabel: isEn ? "Contact us" : isEs ? "Contacta con nosotros" : "Skontaktuj się z nami",
    },
    {
      title: isEn
        ? "Neighbour disputes (odours, noise, aggressive behaviour)"
        : isEs
        ? "Conflictos vecinales (olores, ruido, agresión)"
        : "Spory sąsiedzkie (zapachy, hałas, agresywne zachowanie zwierzęcia)",
      description: isEn
        ? "In your neighbourhood there is an animal that constantly makes noise, produces unpleasant odours or is aggressive? Or do you keep an animal that is unfairly accused of nuisance (noise, odours) or aggression?"
        : isEs
        ? "¿En tu vecindario hay un animal que hace ruido, produce olores o es agresivo? ¿O tienes un animal acusado injustamente de molestias (ruido, olores) o agresión?"
        : "W Twoim sąsiedztwie jest zwierzę, które nieustannie hałasuje, wydziela przykre zapachy lub jest agresywne? A może chowasz zwierzę, które jest niesłusznie posądzane o imisje (hałas, zapachy) lub agresję?",
      priceFrom: isEn ? "Consultation from PLN 400" : isEs ? "Consulta desde 400 PLN" : "Konsultacja od 400 zł",
      ctaLabel: isEn ? "Contact us" : isEs ? "Contacta con nosotros" : "Skontaktuj się z nami",
    },
    {
      title: isEn ? "Inheritance and animals" : isEs ? "Herencias y animales" : "Spadki a zwierzęta",
      description: isEn
        ? "Are you wondering how to ensure good care and maintenance of an animal in case of your death?"
        : isEs
        ? "¿Te preguntas cómo asegurar un buen cuidado y mantenimiento del animal en caso de tu fallecimiento?"
        : "Zastanawiasz się, jak zapewnić dobrą opiekę i utrzymanie zwierzęcia, na wypadek Twojej śmierci?",
      priceFrom: isEn ? "Consultation from PLN 450" : isEs ? "Consulta desde 450 PLN" : "Konsultacja od 450 zł",
      ctaLabel: isEn ? "Contact us" : isEs ? "Contacta con nosotros" : "Skontaktuj się z nami",
    },
    {
      title: isEn ? "Mediation in animal-related matters" : isEs ? "Mediación en asuntos de animales" : "Mediacje w sprawach dotyczących zwierząt",
      description: isEn
        ? "Court is not the only way to resolve an animal-related dispute. We act as mediators in animal-related conflicts. Mediation allows an out-of-court settlement (with the force of a court judgment) with full confidentiality, minimal formalities and speed (a settlement may be concluded even in one session scheduled within one week of the request). Note: If we provide legal advice to one party, we can act as their attorney (including in mediation), but not as the mediator."
        : isEs
        ? "El tribunal no es la única vía. Actuamos como mediadores en conflictos relacionados con animales. La mediación permite un acuerdo extrajudicial (con fuerza de sentencia) con confidencialidad, pocas formalidades y rapidez (puede cerrarse incluso en una sesión en una semana). Nota: Si damos asesoramiento legal a una parte, podemos ser sus representantes, pero no mediadores."
        : "Sąd to nie jest jedyny sposób na rozwiązanie sporu dotyczącego zwierząt. Jesteśmy mediatorami skonflitowanych stron w sprawach dotyczących zwierząt. Mediacja pozwala na pozasądowe, ugodowe zakończenie sporu (ugoda ma moc wyroku sądowego), przy zachowaniu pełnej poufności, minimalnych formalności oraz szybkości postępowania (ugoda może zostać zawarta nawet w ciągu jednej sesji, wyznaczanej w ciągu jednego tygodnia od dnia wpływu wniosku o mediację). Uwaga! Udzielenie porady prawnej jednej ze stron, oznacza, że możemy być pełnomocnikami tej strony (w tym w mediacji), ale już nie mediatorami.",
      priceFrom: isEn ? "Mediation from PLN 500" : isEs ? "Mediación desde 500 PLN" : "Mediacje od 500 zł",
      ctaLabel: isEn ? "Contact us about mediation" : isEs ? "Contacta por mediación" : "Skontaktuj się z nami w sprawie mediacji",
    },
    {
      title: isEn ? "Other civil cases involving animals" : isEs ? "Otros asuntos civiles de animales" : "Inne sprawy cywilne dotyczące zwierząt",
      description: isEn
        ? "Do you have another animal-related matter? Write briefly (up to 5 sentences) what happened — we will tell you if and how it can be handled (and whether it makes economic sense)."
        : isEs
        ? "¿Tienes otro asunto relacionado con animales? Escribe brevemente (hasta 5 frases) qué pasó — te diremos si y cómo se puede abordar (y si compensa)."
        : "Masz inną sprawę dotyczącą zwierząt? Napisz krótko (do 5 zdań) co się stało – powiemy, czy i jak da się to ugryźć (i czy to się w ogóle opłaca).",
      priceFrom: isEn ? "Consultation from PLN 350" : isEs ? "Consulta desde 350 PLN" : "Konsultacja od 350 zł",
      ctaLabel: isEn ? "Describe the case" : isEs ? "Describe el caso" : "Opisz sprawę",
    },
  ];

  return (
    <main>
      <section>
        <div className="container">
          <h2 className="section-heading">{pageTitle}</h2>

          <p className="section-intro">{pageIntro}</p>

          <div className="forwhom-grid">
            {cards.map((c) => (
              <div className="card" key={c.title}>
                <h3>{c.title}</h3>
                <p className="muted">{c.description}</p>

                <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
                  {c.priceFrom}
                </p>

                <div style={{ marginTop: 12 }}>
                  {c.href ? (
                    <a href={c.href} className="link-button">
                      {c.ctaLabel}
                    </a>
                  ) : (
                    <a href="/#kontakt" className="link-button">
                      {c.ctaLabel}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};